import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { getSnapshot } from 'mobx-state-tree'
import { Box, capitalize, IconButton, Stack } from '@mui/material'
import {
  ArrowForwardRounded,
  ReplayRounded,
  StopCircleRounded,
  UpdateRounded,
} from '@mui/icons-material'
import { GridCellParams } from '@mui/x-data-grid-pro'
import { Link } from 'react-router-dom'

import { useStores } from '@trivie/core'
import { DataTable } from '@trivie/ui-web/components/data-table'
import { initColumns } from '@trivie/ui-web/components/data-table/configure'
import { Toolbar } from '@trivie/ui-web/components/table-toolbar'
import { NameCell } from '@trivie/ui-web/components/table-cells/name-cell/name-cell'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'
import { ExtendAssignment } from '@trivie/ui-web/components/extend-dialog'
import { Text } from '@trivie/ui-web/components/text'
import { Button } from '@trivie/ui-web/components/button'
import { color } from '@trivie/ui-web/theme'

import { SubscriptionChip } from './subscription-chip'

type Variant = 'all' | 'product' | 'overview'

interface SubscriptionPurchasesTableProps {
  variant: Variant
  productId?: number
}

export const SubscriptionPurchasesTable = observer(
  ({ variant, productId }: SubscriptionPurchasesTableProps) => {
    const { subscriptionStore, manageUserStore } = useStores()
    const { permissions } = manageUserStore

    const confirm = useConfirm()

    const [selectedRows, setSelectedRows] = useState<any>([])
    const [tableActions, setTableActions] = useState<any>([])
    const [showExtend, setShowExtend] = useState(false)

    const showProduct = variant === 'all'
    const showActions = variant !== 'overview'
    const isOverview = variant === 'overview'

    useEffect(() => {
      subscriptionStore.getPurchases(productId)
    }, [productId])

    const allData = subscriptionStore.purchases.map((p) => ({
      ...getSnapshot(p),
      status: p.status,
    }))
    const rows = isOverview ? allData.slice(0, 5) : allData

    const colDefs: any[] = []

    const productCol = {
      field: 'product_title',
      headerName: 'Subscription',
      description: 'Subscription title',
      data_type: 'str',
      width: 200,
      renderCell: (params: GridCellParams) => (
        <Link
          style={{
            display: 'block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
          to={`/activities/subscriptions/${params.row.product_id}/overview`}
        >
          {params.formattedValue as string}
        </Link>
      ),
    }

    colDefs.push({
      field: 'user',
      headerName: 'Name',
      description: 'Subscriber name',
      data_type: 'str',
      width: 250,
      renderCell: (params: GridCellParams) => {
        return <NameCell params={params} url={`/people/users/${params.row.user_id}/overview`} />
      },
    })

    if (showProduct) {
      colDefs.push(productCol)
    }

    colDefs.push(
      {
        field: 'transaction_id',
        headerName: 'Transaction ID',
        description: 'Transaction ID',
        data_type: 'str',
        width: 150,
      },
      {
        field: 'customer_id',
        headerName: 'Customer ID',
        description: 'Customer ID',
        data_type: 'str',
        width: 130,
      },
      {
        field: 'purchase_date',
        headerName: 'Purchase Date',
        description: 'Date purchased',
        data_type: 'datetime',
        width: 150,
      },
      {
        field: 'expiration_date',
        headerName: 'Expires On',
        description: 'Expiration date',
        data_type: 'datetime',
        width: 150,
      },
      {
        field: 'source',
        headerName: 'Source',
        description: 'Purchase source',
        data_type: 'str',
        width: 120,
        possible_values: ['Gift', 'Purchase', 'Direct'],
        operators: ['eq', 'ne'],
        renderCell: (params: GridCellParams) => {
          if (params.value === 'test') {
            return <SubscriptionChip status="test" />
          }
          return capitalize(params.value as string)
        },
      },
    )

    colDefs.push({
      field: 'status',
      headerName: 'Status',
      description: 'Purchase status',
      data_type: 'str',
      possible_values: ['Active', 'Expired', 'Expiring', 'Failed'],
      operators: ['eq', 'ne'],
      width: 120,
      renderCell: (params: GridCellParams) => <SubscriptionChip status={params.value as string} />,
    })

    const { columns: baseColumns } = initColumns(colDefs)

    const columns = [
      ...baseColumns,
      {
        field: '_action',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        resizable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params: GridCellParams) => (
          <Link to={`/activities/subscriptions/purchases/${params.row.id}/overview`}>
            <IconButton size="small">
              <ArrowForwardRounded fontSize="small" />
            </IconButton>
          </Link>
        ),
      },
    ]

    const getActions = () => {
      const selectedStatus = rows.find((p) => p.id === selectedRows[0])?.status
      const singleFailed = selectedRows.length === 1 && selectedStatus === 'Failed'
      const allActiveOrExpiring =
        selectedRows.length > 0 &&
        selectedRows.every((id: number) => {
          const purchase = rows.find((p) => p.id === id)
          return purchase?.status === 'active' || purchase?.status === 'expiring'
        })

      return [
        {
          label: 'Extend',
          icon: UpdateRounded,
          disabled:
            selectedRows.length !== 1 ||
            rows.find((p) => p.id === selectedRows[0])?.expiration_date == null ||
            selectedStatus === 'failed' ||
            selectedStatus === 'expired' ||
            !permissions?.permissions.some((p) => p.code === 'can_edit_purchase'),

          onClick: () => setShowExtend(true),
        },
        {
          label: 'Reattempt',
          icon: ReplayRounded,
          disabled:
            !singleFailed || !permissions?.permissions.some((p) => p.code === 'can_edit_purchase'),

          onClick: () => {
            confirm({
              title: 'Reattempt Purchase',
              dialogText: 'Are you sure you want to reattempt this purchase?',
            })
              .then(async () => {
                const success = await subscriptionStore.reattemptPurchase(selectedRows[0])
                setSelectedRows([])
                if (!success) {
                  confirm({
                    danger: true,
                    title: 'Error',
                    dialogText: 'There was an issue reattempting this purchase',
                    hideCancelButton: true,
                  })
                } else {
                  subscriptionStore.getPurchases()
                }
              })
              .catch(() => {})
          },
        },
        {
          label: 'Revoke Subscription',
          color: 'danger',
          icon: StopCircleRounded,
          disabled:
            selectedRows.length !== 1 ||
            !allActiveOrExpiring ||
            !permissions?.permissions.some((p) => p.code === 'can_delete_purchase'),
          onClick: () => {
            confirm({
              danger: true,
              title: 'Revoke Subscription',
              dialogText:
                'Are you sure you want to revoke the selected subscription(s)? This action cannot be undone.',
            })
              .then(async () => {
                const success = await subscriptionStore.revokeSubscription(selectedRows[0])
                setSelectedRows([])
                if (!success) {
                  confirm({
                    danger: true,
                    title: 'Error',
                    dialogText: 'There was an issue revoking the selected subscription(s)',
                    hideCancelButton: true,
                  })
                } else {
                  subscriptionStore.getPurchases()
                }
              })
              .catch(() => {})
          },
        },
      ]
    }

    useEffect(() => {
      if (showActions) {
        setTableActions(getActions())
      }
    }, [selectedRows])

    return (
      <Box mb="40px">
        <DataTable
          // loading={subscriptionStore.loadingPurchases}
          toolbar={
            isOverview ? (
              <Stack flex={1} direction="row" justifyContent="space-between" alignItems="center">
                <Text component="div" variant="body1Emphasized" text="Recent Purchases" />
                <Link
                  to={`/activities/subscriptions/${productId}/subscribers`}
                  style={{ textDecoration: 'none' }}
                >
                  <Button
                    style={{ color: color.shade70, fontSize: 12 }}
                    variant="text"
                    endIcon={<ArrowForwardRounded htmlColor={color.shade70} fontSize="small" />}
                    text="View All Subscribers"
                  />
                </Link>
              </Stack>
            ) : (
              <Toolbar selected={selectedRows} actions={tableActions} />
            )
          }
          searchPlaceholder={variant === 'product' ? 'Search subscribers' : 'Search subscriptions'}
          columns={columns as any}
          rows={rows}
          getRowId={(row) => row.id}
          onRowSelectionModelChange={showActions ? (param) => setSelectedRows(param) : undefined}
          rowSelectionModel={selectedRows}
          checkboxSelection={!isOverview}
          hidePagination={isOverview}
          hideActions={isOverview}
          disableSearch={isOverview}
          onSaveFilters={() => {}}
          initialState={{
            sorting: {
              sortModel: [{ field: 'purchase_date', sort: 'desc' }],
            },
          }}
          disableColumnPinning={false}
          pinnedColumns={{ right: ['_action'] }}
          sx={{
            '& .MuiDataGrid-cell--pinnedRight': {
              borderLeft: 'none',
              boxShadow: 'none',
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: 'rgb(245,245,245) !important',
            },
            '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: 'rgb(239, 242, 254)!important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: 'rgb(230, 235, 253)!important',
            },
            '& .MuiDataGrid-columnHeader--pinnedRight': {
              borderLeft: 'none',
              boxShadow: 'none',
              backgroundColor: 'inherit',
            },
            '& .MuiDataGrid-columnHeader[data-field="_action"] .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-columnHeader--last .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
          }}
        />
        {showActions && showExtend && (
          <ExtendAssignment
            title="Extend Subscription"
            open={showExtend}
            initial={rows.find((p) => p.id === selectedRows[0])?.expiration_date}
            onClose={() => {
              setShowExtend(false)
              setSelectedRows([])
            }}
            onExtend={async ({ endDateTime }) => {
              const success = await subscriptionStore.extendPurchases(selectedRows, endDateTime)
              setShowExtend(false)
              setSelectedRows([])
              if (!success) {
                confirm({
                  danger: true,
                  title: 'Error',
                  dialogText: 'There was an issue extending this purchase',
                  hideCancelButton: true,
                })
              } else {
                subscriptionStore.getPurchases()
              }
            }}
          />
        )}
      </Box>
    )
  },
)
